import { SquareNewWindowIcon } from "@codat/orchard-ui";
import classNames from "classnames";
import { useProducts } from "contexts/ProductsContext";
import useScroll from "hooks/useScroll";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { paths } from "components/App/paths";
import NavButton from "components/NavButton";
import {
  NavPermissionMap,
  canViewNavGroupLink,
  getNavGroupFirstPagePathOrDefault,
} from "components/NavContainer/utils";
import SyncForCommerceModal from "components/SyncForCommerceModal";
import SyncForExpensesModal from "components/SyncForExpensesModal";

import styles from "./NavBar.module.scss";

interface NavBarProps {
  isLoading: boolean;
  navPermissionMap?: NavPermissionMap;
}

export const NavBar: React.FC<NavBarProps> = ({
  isLoading,
  navPermissionMap,
  ...props
}) => {
  const [showSyncCommerceModal, setShowSyncCommerceModal] = useState(false);
  const [showSyncForExpensesModal, setShowSyncForExpensesModal] =
    useState(false);
  const { isScrolled } = useScroll(document.getElementById("page-content"));
  const { featureStatus } = useFeatureStatus();
  const { location } = useHistory();
  const {
    productsEnabledStatus: {
      isSyncForCommerceEnabled,
      isSyncForExpensesV1Enabled,
      isSyncForExpensesV2Enabled,
      isSpendInsightsEnabled,
    },
  } = useProducts();

  const isSpendInsightsTableEnabled =
    isSpendInsightsEnabled &&
    featureStatus?.["PortalUI.ShowSpendInsightsTable"];

  const canViewMonitor = canViewNavGroupLink(navPermissionMap?.monitor);
  const canViewSettings = canViewNavGroupLink(navPermissionMap?.settings);
  const canViewDevelopers = canViewNavGroupLink(navPermissionMap?.developers);

  const monitorFirstPagePath = getNavGroupFirstPagePathOrDefault(
    navPermissionMap?.monitor
  );

  return (
    <nav
      className={classNames(styles.navBar, { [styles.dropShadow]: isScrolled })}
      data-testid="navbar"
      {...props}
    >
      <ul>
        {navPermissionMap?.home.permission && (
          <li className={styles.navItem}>
            <NavButton
              size="small"
              isSkeleton={isLoading}
              eventProperties={{ page: "Home" }}
              label="Home"
              isActive={location.pathname === paths.HOME}
              to={paths.HOME}
              data-testid="nav-home"
            />
          </li>
        )}
        {isSpendInsightsTableEnabled && (
          <li className={styles.navItem}>
            <NavButton
              size="small"
              isSkeleton={isLoading}
              label="Spend Insights"
              isActive={location.pathname.startsWith(paths.SPEND_INSIGHTS)}
              to={paths.SPEND_INSIGHTS}
              data-testid="nav-spend-insights"
            />
          </li>
        )}
        {isSyncForCommerceEnabled && (
          <li className={styles.navItem}>
            <NavButton
              size="small"
              isSkeleton={isLoading}
              eventProperties={{ page: "Sync For Commerce" }}
              label="Sync For Commerce"
              isActive={location.pathname === paths.products.SYNC_FOR_COMMERCE}
              to={paths.products.SYNC_FOR_COMMERCE}
              data-testid="nav-sync-commerce"
            />
          </li>
        )}
        {(isSyncForExpensesV1Enabled || isSyncForExpensesV2Enabled) && (
          <li className={styles.navItem}>
            <NavButton
              size="small"
              isSkeleton={isLoading}
              eventProperties={{ page: "Sync For Expenses" }}
              label="Sync For Expenses"
              isActive={location.pathname === paths.products.SYNC_FOR_EXPENSES}
              to={paths.products.SYNC_FOR_EXPENSES}
              data-testid="nav-sync-expenses"
            />
          </li>
        )}
        {navPermissionMap?.companies.permission && (
          <li className={styles.navItem}>
            <NavButton
              isSkeleton={isLoading}
              eventProperties={{ page: "Companies" }}
              isActive={
                location.pathname.startsWith(paths.COMPANIES) ||
                !!location.pathname.match(/\/assess\/./)
              }
              label="Companies"
              to={paths.COMPANIES}
              data-testid="nav-companies"
            />
          </li>
        )}
        {canViewMonitor && (
          <li className={styles.navItem}>
            <NavButton
              isSkeleton={isLoading}
              eventProperties={{ page: "Monitor" }}
              isActive={Object.values(paths.monitor).some(
                (path) => path === location.pathname
              )}
              label="Monitor"
              to={monitorFirstPagePath}
              data-testid="nav-monitor"
            />
          </li>
        )}
        {canViewSettings && (
          <li className={styles.navItem}>
            <NavButton
              isSkeleton={isLoading}
              eventProperties={{ page: "Settings" }}
              isActive={Object.values(paths.settings).some((path) => {
                return location.pathname.includes(path);
              })}
              label="Settings"
              to={paths.settings.OVERVIEW}
              data-testid="nav-settings"
            />
          </li>
        )}
        {canViewDevelopers && (
          <li className={styles.navItem}>
            <NavButton
              isSkeleton={isLoading}
              eventProperties={{ page: "Developers" }}
              isActive={Object.values(paths.developers).some((path) => {
                return location.pathname.includes(path);
              })}
              label="Developers"
              to={paths.developers.API_KEYS}
              data-testid="nav-developers"
            />
          </li>
        )}
        <li className={styles.documentation}>
          <NavButton
            data-testid="nav-documentation"
            isSkeleton={isLoading}
            eventProperties={{ page: "Documentation" }}
            href="https://docs.codat.io"
            color="interface"
            variant="secondary"
            target="_blank"
            aria-label="Documentation"
          >
            Documentation <SquareNewWindowIcon className={styles.icon} />
          </NavButton>
        </li>
      </ul>
      {showSyncCommerceModal && (
        <SyncForCommerceModal onClose={() => setShowSyncCommerceModal(false)} />
      )}
      {showSyncForExpensesModal && (
        <SyncForExpensesModal
          onClose={() => setShowSyncForExpensesModal(false)}
        />
      )}
    </nav>
  );
};

export default NavBar;
